import { AuthenticatedApiRequest } from '@goldspotdiscoveries/auth';
import {
  getDrillHoleListParams,
  DrillHoleList,
  DrillHoleObject,
  ReportList,
  VeinNetObject,
  RoQENetObject,
  PercentageReportObject,
  PercentageReportLightObject,
  RqdObject,
} from '../../TypeScript/interfaces';
import { getJsonFile } from './files';
import { MLEnum } from '../../TypeScript/enums';
import { MLType } from '../../TypeScript/types';

export const getDrillHolesList = (
  company: string,
  project: string,
  queryParams?: getDrillHoleListParams
): Promise<DrillHoleList> => {
  return new Promise((resolve, reject) => {
    const params: string[] = Object.keys(queryParams || {}).map(
      (key: string): string => {
        const value: string = (queryParams || ({} as any))[key];
        if (typeof value === 'undefined' || value === null || value === '') {
          return '';
        } else {
          return `${key}=${value}`;
        }
      }
    );
    AuthenticatedApiRequest({
      method: 'get',
      apiName: 'Projects',
      path: `/${company}/${project}/drillholes?${params.join('&')}`,
    })
      .then((result: any) => {
        const drillHoleList: DrillHoleList = result;
        resolve(drillHoleList);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getDrillHole = (
  company: string,
  project: string,
  drillholeId: string
): Promise<DrillHoleObject> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'get',
      apiName: 'Projects',
      path: `/${company}/${project}/drillholes/${drillholeId}`,
    })
      .then((result: any) => {
        const drillHole: DrillHoleObject = result;
        resolve(drillHole);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const putDrillHole = (
  company: string,
  project: string,
  drillholeId: string
): Promise<{ message: string }> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'put',
      apiName: 'Projects',
      path: `/${company}/${project}/drillholes`,
      postData: {
        body: {
          name: drillholeId,
        },
      },
    })
      .then((result: any) => {
        const response: { message: string } = result;
        resolve(response);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const postDrillHole = (
  company: string,
  project: string,
  drillHoleId: string,
  requestBody: Record<string, string>
): Promise<{ message: string }> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'post',
      apiName: 'Projects',
      path: `/${company}/${project}/drillholes/${drillHoleId}`,
      postData: {
        body: requestBody,
      },
    })
      .then((result: any) => {
        const response: { message: string } = result;
        resolve(response);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getDrillHolePath = (
  path: string
): Promise<{ items: any[]; next_offset_token: string }> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'get',
      apiName: 'Files',
      path: `/${path}`,
    })
      .then((result: any) => {
        const response: { items: any[]; next_offset_token: string } = result;
        resolve(response);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Trigger the export of ML results to S3
 *
 * ML results are veinnet, roqenet, etc.
 */
export const exportMLToFile = (
  company: string,
  project: string,
  drillholeId: string | number,
  mLType: MLType
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const mLPath: { type: string } = { type: 'vein' };
    switch (mLType) {
      case MLEnum.veinnet:
        mLPath.type = 'vein';
        break;
      case MLEnum.roqenet:
      case MLEnum.fcnet:
        mLPath.type = 'classifications';
        break;
      case MLEnum.percentageReport:
        mLPath.type = 'percentage_report';
        break;
      case MLEnum.percentageReportLight:
        mLPath.type = 'percentage_report_light';
        break;
      case MLEnum.veinFrequency:
        mLPath.type = 'vein_freq_report';
        break;
      case MLEnum.fractureFrequency:
        mLPath.type = 'fracture_freq_report';
        break;
      case MLEnum.rqd:
        mLPath.type = 'rqd_report';
        break;
      default:
        console.error(
          `Error in getMLResultsFile: MLtype ${mLType} not recognized`
        );
    }
    AuthenticatedApiRequest({
      method: 'get',
      apiName: 'Projects',
      path: `/${company}/${project}/drillholes/${drillholeId}/export/${mLPath.type}`,
    })
      .then((result: any) => {
        resolve();
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Attempt to retrieve the ML results from S3 up to 3 times
 *
 * second and third attempts are only if first and second attempts fail
 *
 * Rationale: export to S3 may not be complete on first fetch, so
 * subsequent attempts may be required.
 */
const tryMultipleTimes = (
  filePath: string,
  drillholeName: string
): Promise<
  | ReportList<VeinNetObject>
  | ReportList<RoQENetObject>
  | ReportList<PercentageReportObject>
  | ReportList<PercentageReportLightObject>
  | ReportList<RqdObject>
> => {
  return new Promise((resolve, reject) => {
    const attempts = {
      qty: 0,
      max: 3,
    };
    async function getFile(): Promise<
      | ReportList<VeinNetObject>
      | ReportList<RoQENetObject>
      | ReportList<PercentageReportObject>
      | ReportList<PercentageReportLightObject>
      | ReportList<RqdObject>
    > {
      return await getMLResultsFile(filePath, drillholeName)
        .then(
          (
            resultsList:
              | ReportList<VeinNetObject>
              | ReportList<RoQENetObject>
              | ReportList<PercentageReportObject>
              | ReportList<PercentageReportLightObject>
              | ReportList<RqdObject>
          ) => {
            return resultsList;
          }
        )
        .catch((err) => {
          if (attempts.qty < attempts.max) {
            attempts.qty++;
            return getFile();
          } else {
            const err = new Error();
            throw err;
          }
        });
    }
    getFile()
      .then(
        (
          resultsList:
            | ReportList<VeinNetObject>
            | ReportList<RoQENetObject>
            | ReportList<PercentageReportObject>
            | ReportList<PercentageReportLightObject>
            | ReportList<RqdObject>
        ) => {
          resolve(resultsList);
        }
      )
      .catch((err) => {
        reject();
      });
  });
};

/**
 * Get ML Results from S3 using the files API
 */
const getMLResultsFile = (
  filePath: string,
  drillholeName: string
): Promise<
  | ReportList<VeinNetObject>
  | ReportList<RoQENetObject>
  | ReportList<PercentageReportObject>
  | ReportList<PercentageReportLightObject>
  | ReportList<RqdObject>
> => {
  return new Promise((resolve, reject) => {
    const path = filePath.replace('{drillholeName}', drillholeName);
    getJsonFile(path)
      .then(
        (
          resultsList:
            | ReportList<VeinNetObject>
            | ReportList<RoQENetObject>
            | ReportList<PercentageReportObject>
            | ReportList<PercentageReportLightObject>
            | ReportList<RqdObject>
        ) => {
          resolve(resultsList);
        }
      )
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Get ML results from S3, and trigger their export to S3 if the file does
 * not exist yet or if the user opts not to use cached data (forceRefresh)
 */
export const getMlResults = (
  company: string,
  project: string,
  drillholeName: string,
  drillholeId: string | number,
  mLType: MLType,
  forceRefresh: boolean = false,
  filePath: string = ''
): Promise<
  | ReportList<VeinNetObject>
  | ReportList<RoQENetObject>
  | ReportList<PercentageReportObject>
  | ReportList<PercentageReportLightObject>
  | ReportList<RqdObject>
> => {
  return new Promise((resolve, reject) => {
    const getFreshResults = () => {
      exportMLToFile(company, project, drillholeId, mLType)
        .then(() => {
          tryMultipleTimes(filePath, drillholeName)
            .then(
              (
                resultsList:
                  | ReportList<VeinNetObject>
                  | ReportList<RoQENetObject>
                  | ReportList<PercentageReportObject>
                  | ReportList<PercentageReportLightObject>
                  | ReportList<RqdObject>
              ) => {
                resolve(resultsList);
              }
            )
            .catch((err) => {
              reject(err);
            });
        })
        .catch((err) => {
          reject(err);
        });
    };
    if (!forceRefresh) {
      // allow cache
      getMLResultsFile(filePath, drillholeName)
        .then(
          (
            resultsList:
              | ReportList<VeinNetObject>
              | ReportList<RoQENetObject>
              | ReportList<PercentageReportObject>
              | ReportList<PercentageReportLightObject>
              | ReportList<RqdObject>
          ) => {
            resolve(resultsList);
          }
        )
        .catch((err) => {
          getFreshResults();
        });
    } else {
      getFreshResults();
    }
  });
};

export const deleteDrillhole = (
  company: string,
  projectId: string,
  drillholeId: string
) =>
  AuthenticatedApiRequest({
    method: 'delete',
    apiName: 'Projects',
    path: `/${company}/${projectId}/drillholes/${drillholeId}`,
  }) as unknown as Promise<{ message: string; delete: Record<string, number> }>;
